import * as React from "react";
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import {
    Create,
    SimpleForm,
    TextInput,
    ImageInput,
    SelectInput,
    ImageField,
    ReferenceArrayInput,
    ReferenceInput,
    SelectArrayInput,
    FileInput,
    FileField,
    required,
    DateTimeInput,
    useGetList,
    useLocaleState,
    useTranslate
} from 'react-admin';
import { DisplayStatus } from '../../shared/enums/display-status.enum';
import { Status } from "../../shared/enums/status.enum";
import { TimePeriod } from '../../shared/interfaces/timePeriod.interface';
import GetTimePeriodRange from '../../shared/services/timePeriod.services';
import { locales } from  '../../i18n/languges'
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { FieldValues } from 'react-hook-form';

// Helper function to safely get nested translated field
const getTranslatedField = (record: any, field: string, locale: string): string => {
    if (!record) return '';
    const parts = field.split('.');
    let value: any = record;
    for (const part of parts) {
        if (value && typeof value === 'object' && part in value) {
            value = value[part];
        } else {
            return '';
        }
    }
    if (value == null) return '';
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        if ('sv' in value && typeof value.sv === 'object' && value.sv !== null && !Array.isArray(value.sv)) {
            value = value.sv;
        }
        if (locale in value && value[locale] != null && typeof value[locale] === 'string') return value[locale];
        for (const fb of ['sv', 'en', 'de']) {
            if (fb in value && value[fb] != null && typeof value[fb] === 'string') return value[fb];
        }
        for (const key in value) {
            if (typeof value[key] === 'string' && value[key]) return value[key];
        }
        return '';
    }
    if (typeof value === 'string') return value;
    return String(value);
};

export const PathCreate = () => {
    const [locale] = useLocaleState();
    const [selectedLocale, setSelectedLocale] = useState(locale || locales[0]);
    const translate = useTranslate();
    const timePeriods = useGetList<TimePeriod>(
        "timeperiods",
        {
            pagination: { page: 1, perPage: 100 },
            sort: { field: "id", order: "ASC" },
        },
    );
    const [displayValue, setDisplayValue] = useState(DisplayStatus.Display);
    const [selectedTimePeriodId, setSelectedTimePeriodId] = useState<number>(0);
    const selectedTimePeriod = GetTimePeriodRange(
        selectedTimePeriodId,
        timePeriods.data || {}
    );
    const displayOptions = Object.keys(DisplayStatus).map(key => ({
        id: DisplayStatus[key],
        name: `resources.enums.displayStatus.${key}`,
    }));

    const statusOptions = Object.keys(Status).map((key) => ({
        id: Status[key],
        name: key === Status.notSet ? '---' : translate(`resources.paths.fields.${key}`),
      }));

    const schema = yup
        .object()
        .shape({
            name: yup.object().shape({
                en: yup.string().required()
            }),
        number: yup.number().nullable(),
        length: yup.number().nullable().positive()
            .transform((_value, originalValue) => {
                if (originalValue === '' || originalValue === null || originalValue === undefined) return null;
                return Number(String(originalValue).replace(',', '.'));
            }),
        difficulty: yup.string().default('easy'),
        status: yup.string().default('notSet'),
        pathCategoryId: yup.number().required(),
        area_ids: yup.array().of(yup.number()).nullable(),
        gpx: yup.mixed().required(),
        display: yup.string().default('display'),
        timePeriodId: yup.string().nullable().when('display', {
            is: (value: string | undefined) => value === DisplayStatus.Scheduled,
            then: schema => schema.required('Time period can\'t be empty if display is scheduled'),
            otherwise: schema => schema.nullable()
        }),
    });
    const resolver = yupResolver<FieldValues>(schema);

    // Helper: renders a TextInput for ALL locales but only shows the selected one.
    const TField = ({ source, ...props }: { source: string; [key: string]: any }) => (
        <Box sx={{ width: '100%' }}>
            {locales.map(l => (
                <Box key={`${source}.${l}`} sx={{ display: selectedLocale === l ? undefined : 'none' }}>
                    <TextInput source={`${source}.${l}`} fullWidth {...props} />
                </Box>
            ))}
        </Box>
    );

    // Section header with divider line
    const SectionHeader = ({ label }: { label: string }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', mt: 2, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontSize: '1.1rem', fontWeight: 700, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                {label}
            </Typography>
            <Divider sx={{ flexGrow: 1, borderBottomWidth: 2 }} />
        </Box>
    );

    return (
        <Create>
            <SimpleForm sanitizeEmptyValues resolver={resolver}>
                {/* ── Language tabs ── */}
                <Tabs
                    value={selectedLocale}
                    onChange={(_, v) => setSelectedLocale(v)}
                    sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', width: '100%' }}
                >
                    {locales.map(l => (
                        <Tab key={l} value={l} label={l.toUpperCase()} />
                    ))}
                </Tabs>

                {/* ── Row 1: Name + Number + Category (3 cols) ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                    <TField source="name" label="Name" />
                    <TextInput source="number" fullWidth />
                    <ReferenceInput source="pathCategoryId" reference="pathcategories">
                        <SelectInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="resources.paths.fields.category" fullWidth />
                    </ReferenceInput>
                </Box>

                {/* ══ STATUS SECTION ══ */}
                <SectionHeader label="Status" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                    <SelectInput source={'status'} label="resources.paths.fields.status" defaultValue={'notSet'} choices={statusOptions} fullWidth />
                    <Box>
                        <SelectInput
                            source="display"
                            choices={displayOptions}
                            defaultValue={DisplayStatus.Display}
                            fullWidth
                            onChange={(e) => setDisplayValue(e.target.value as DisplayStatus)}
                        />
                        {displayValue === DisplayStatus.Scheduled && (
                            <ReferenceInput
                                source="timePeriodId"
                                reference="timeperiods"
                                onChange={(e) => setSelectedTimePeriodId(e.target.value)}
                            >
                                <SelectInput
                                    optionText="name"
                                    helperText={selectedTimePeriod}
                                    fullWidth
                                />
                            </ReferenceInput>
                        )}
                    </Box>
                    <DateTimeInput source="groomedAt" defaultValue={new Date()} fullWidth />
                </Box>

                {/* ══ INFORMATION SECTION ══ */}
                <SectionHeader label="Information" />

                {/* Difficulty + Length */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                    <SelectInput source="difficulty" defaultValue={'easy'} fullWidth choices={[
                        { id: 'veryEasy', name: 'resources.paths.fields.veryEasy' },
                        { id: 'easy', name: 'resources.paths.fields.easy' },
                        { id: 'medium', name: 'resources.paths.fields.medium' },
                        { id: 'hard', name: 'resources.paths.fields.hard' },
                    ]} />
                    <TextInput source="length" label="Length (km)" fullWidth />
                </Box>

                {/* Start + End */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                    <TField source="start" label="Start name" />
                    <TField source="end" label="End name" />
                </Box>

                {/* Description + Areas/POIs/Tags */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%', alignItems: 'start' }}>
                    <TField source="description" rows={8} multiline fullWidth />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <ReferenceArrayInput source="area_ids" reference="areas">
                            <SelectArrayInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="resources.paths.fields.areas" defaultValue={[]} fullWidth />
                        </ReferenceArrayInput>
                        <ReferenceArrayInput source="poi_ids" reference="pois">
                            <SelectArrayInput label="resources.paths.fields.relatedPois" fullWidth optionText={record => {
                                if (record.areas && record.areas.length > 0) {
                                    const areaNames = record.areas.map(area => getTranslatedField(area, 'name', selectedLocale)).join(', ');
                                    return `${getTranslatedField(record, 'name', selectedLocale)} (${areaNames})`;
                                }
                                return getTranslatedField(record, 'name', selectedLocale);
                            }} defaultValue={[]} />
                        </ReferenceArrayInput>
                        <ReferenceArrayInput source="tag_ids" reference="tags">
                            <SelectArrayInput optionText={(record) => getTranslatedField(record, 'name', selectedLocale)} label="resources.paths.fields.tags" fullWidth />
                        </ReferenceArrayInput>
                    </Box>
                </Box>

                {/* ══ FILES SECTION ══ */}
                <SectionHeader label="Files" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, width: '100%' }}>
                    <FileInput source="gpx" validate={[required()]} label="GPX file">
                        <FileField source="src" title="title" />
                    </FileInput>
                    <ImageInput source="medias" multiple={true} label="Medias">
                        <ImageField source="src" title="title" />
                    </ImageInput>
                </Box>

                {/* ══ SOCIAL MEDIA SECTION ══ */}
                <SectionHeader label="Social media links" />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, width: '100%' }}>
                    <TextInput source="socialMedia.instagramUrl" label="Instagram url" fullWidth />
                    <TextInput source="socialMedia.facebookUrl" label="Facebook url" fullWidth />
                    <TextInput source="socialMedia.linkedinUrl" label="Linkedin url" fullWidth />
                    <TextInput source="socialMedia.twitterUrl" label="Twitter url" fullWidth />
                    <TextInput source="socialMedia.vimeoUrl" label="Vimeo url" fullWidth />
                    <TextInput source="socialMedia.youtubeUrl" label="Youtube url" fullWidth />
                    <TextInput source="socialMedia.tripadvisorUrl" label="Tripadvisor url" fullWidth />
                </Box>
            </SimpleForm>
        </Create>
    );
}